import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validatePermission } from 'actions';
import { cooperplaceApi } from 'services/api';
import Modal from 'v3/components/Modal';
import Button from 'v3/components/Button';
import { attendancePath, kanbanReturnState, saveAttendanceSession } from './session';
import { canUseOther, otherCandidates } from './otherContract';

export default function OtherAttendance({ context, data, onChanged }) {
  const inputId = useId();
  const navigate = useNavigate();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [plate, setPlate] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const generation = useRef(0);
  const pending = useRef(false);
  useEffect(
    () => () => {
      generation.current += 1;
    },
    [],
  );
  const permitted = validatePermission(
    context.item.type === 'PRE_LOAD' ? 'ATENDER_PRECARGA' : 'ATENDER_CARGA',
  );
  const allowed = permitted && canUseOther(data?.operationalUniverse);
  // Aberto pelo card sem veículo apto: abre a busca assim que a consulta libera o atendimento.
  const autoOpened = useRef(false);
  useEffect(() => {
    if (!context.openOther || !allowed || autoOpened.current) return;
    autoOpened.current = true;
    setShow(true);
  }, [context.openOther, allowed]);
  const normalized = plate.trim().replace(/-/g, '').toUpperCase();
  const close = () => {
    generation.current += 1;
    pending.current = false;
    setShow(false);
    setBusy(false);
    setResult(null);
    setError(null);
    setPlate('');
  };
  const run = async (action) => {
    if (!allowed || pending.current) return;
    pending.current = true;
    const version = ++generation.current;
    setBusy(true);
    setError(null);
    try {
      await action(() => generation.current === version);
    } catch (failure) {
      if (generation.current === version)
        setError(
          failure?.response?.data?.message ||
            failure.message ||
            'Não foi possível consultar outro veículo.',
        );
    } finally {
      if (generation.current === version) {
        pending.current = false;
        setBusy(false);
      }
    }
  };
  const search = (event) => {
    event.preventDefault();
    if (!/^[A-Z0-9]{3,7}$/.test(normalized)) return;
    setResult(null);
    run(async (current) => {
      const response = await cooperplaceApi.get(
        `/control-tower/loads/${context.item.loadId}/other-candidates`,
        { params: { plate: normalized } },
      );
      if (current()) setResult(otherCandidates(response.data, context));
    });
  };
  const select = (vehicleId) =>
    run(async (current) => {
      const response = await cooperplaceApi.post(
        `/control-tower/loads/${context.item.loadId}/other-candidates/${vehicleId}/attendance-context`,
      );
      if (!current()) return;
      if (response.data?.mode !== 'OTHER' || response.data.selection?.vehicle?.id !== vehicleId)
        throw new Error('Contexto de atendimento com outro inválido.');
      const record = saveAttendanceSession(
        response.data,
        context,
        kanbanReturnState(location.state, context),
      );
      navigate(
        attendancePath(
          `/${record.loadKind === 'PRE_LOAD' ? 'pre-cargas' : 'cargas'}/${record.loadId}`,
          record,
        ),
      );
    });
  return (
    <>
      <Button
        size="sm"
        disabled={!allowed}
        onClick={() => setShow(true)}
        title={
          allowed
            ? 'Selecionar outro veículo'
            : 'Exige consulta completa, todas as recusas e permissão de atendimento'
        }
      >
        Atender com outro
      </Button>
      <Modal
        show={show && allowed}
        handleClose={close}
        size="lg"
        heading="Atender com outro"
        body={
          <>
            <p>
              {context.item.type === 'PRE_LOAD' ? 'Pré-carga' : 'Carga'} {context.item.loadId}.
              Selecione outro veículo antes de continuar o atendimento.
            </p>
            <p>
              O veículo pode estar fora dos 500 km, mas precisa atender ao perfil da carga, MOPP e
              disponibilidade, sem recusa ativa.
            </p>
            <form onSubmit={search}>
              <label htmlFor={inputId}>Placa do veículo</label>
              <input
                id={inputId}
                className="form-control mb-2"
                value={plate}
                disabled={busy}
                maxLength={8}
                onChange={(event) => {
                  setPlate(event.target.value);
                  setResult(null);
                }}
                placeholder="Digite ao menos 3 caracteres"
              />
              <Button type="submit" disabled={busy || !/^[A-Z0-9]{3,7}$/.test(normalized)}>
                Buscar veículo
              </Button>
            </form>
            {busy && <p role="status">Validando disponibilidade e recusas…</p>}
            {error && (
              <div role="alert">
                <p>{error}</p>
                <Button size="sm" onClick={onChanged}>
                  Atualizar fila
                </Button>
              </div>
            )}
            {result && (
              <div aria-live="polite">
                <p>
                  {result.totalCount
                    ? `${result.totalCount} veículo(s) encontrado(s).`
                    : 'Nenhum outro veículo disponível para essa placa.'}
                </p>
                {result.totalCount > 25 && (
                  <p>Exibindo os primeiros 25. Informe mais caracteres da placa.</p>
                )}
                <ul style={{ paddingLeft: 20 }}>
                  {result.data.map((row) => (
                    <li key={row.vehicleId} style={{ marginBottom: 12 }}>
                      <span>
                        {[row.plate, ...row.wagons.map((w) => w.plate)].join(' / ')} ·{' '}
                        {row.driver ? row.driver.name || 'Motorista cadastrado' : 'Sem motorista'} ·{' '}
                        {(row.distanceMeters / 1000).toLocaleString('pt-BR')} km até a coleta{' '}
                      </span>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() => select(row.vehicleId)}
                        aria-label={`Selecionar outro veículo ${row.plate}`}
                      >
                        Selecionar
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        }
        footer={
          <Button variant="secondary" onClick={close}>
            Cancelar
          </Button>
        }
      />
    </>
  );
}
